import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDown, ArrowUp, ChevronsUpDown, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * DataTable — the one table for the portal.
 *
 * Every list page used to hand-roll its own grid or <table> (Invoices,
 * Payments, admin customer lists, returns…), so alignment, header styling,
 * hover, sorting, empty/loading states and mobile behavior all drifted. This
 * gives them one implementation:
 *
 *  - Semantic <table> on sm+ with a sticky, quiet header row.
 *  - tabular-nums on numeric columns so money/quantities line up.
 *  - Optional per-column click-to-sort (asc / desc / indicator).
 *  - Row click OR row href (renders the whole row as a router <Link>).
 *  - Built-in loading skeleton, error, and empty states.
 *  - Mobile card fallback via `mobileRow` (or a sensible default stack).
 *
 * Columns describe both the header and how to render each cell, keeping the
 * shape declarative and consistent across pages.
 */

export interface Column<T> {
  /** Stable key; also the sort key. */
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  align?: 'left' | 'right' | 'center';
  /** Right-aligns + applies tabular-nums for clean money/number columns. */
  numeric?: boolean;
  /** Provide to make the column sortable. */
  sortValue?: (row: T) => string | number;
  /** Hide this column below the sm breakpoint (data still shows in mobileRow). */
  hideOnMobile?: boolean;
  /** Fixed column width, e.g. '120px'. */
  width?: string;
  className?: string;
  headerClassName?: string;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[] | undefined;
  rowKey: (row: T) => string;
  isLoading?: boolean;
  error?: string | null;
  /** Rendered when there are no rows (and not loading/erroring). */
  empty?: ReactNode;
  onRowClick?: (row: T) => void;
  /** If provided, the row is a router <Link> to this href. */
  rowHref?: (row: T) => string;
  /** Custom mobile card body per row. Falls back to a label/value stack. */
  mobileRow?: (row: T) => ReactNode;
  initialSort?: { key: string; dir: 'asc' | 'desc' };
  /** Screen-reader caption for the table. */
  caption?: string;
  className?: string;
}

type SortState = { key: string; dir: 'asc' | 'desc' } | null;

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  isLoading = false,
  error = null,
  empty,
  onRowClick,
  rowHref,
  mobileRow,
  initialSort,
  caption,
  className,
}: DataTableProps<T>) {
  const [sort, setSort] = useState<SortState>(initialSort ?? null);

  const sorted = useMemo(() => {
    const data = rows ?? [];
    if (!sort) return data;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.sortValue) return data;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...data].sort((a, b) => {
      const av = col.sortValue!(a);
      const bv = col.sortValue!(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [rows, sort, columns]);

  function toggleSort(key: string) {
    setSort((prev) => {
      if (prev?.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });
  }

  const alignClass = (c: Column<T>) =>
    c.align === 'right' || c.numeric ? 'text-right' : c.align === 'center' ? 'text-center' : 'text-left';

  const interactive = !!onRowClick || !!rowHref;

  return (
    <div
      className={cn(
        'rounded-2xl border border-brix-border bg-brix-surface overflow-hidden shadow-card',
        className,
      )}
    >
      {/* ---- States ---- */}
      {isLoading ? (
        <div className="px-4 py-14 text-center text-sm text-brix-muted">
          <Loader2 size={18} className="mr-2 inline-block animate-spin" /> Loading…
        </div>
      ) : error ? (
        <div className="px-4 py-10 text-center text-sm text-red-700">{error}</div>
      ) : sorted.length === 0 ? (
        <div className="px-4 py-14 text-center text-sm text-brix-muted">
          {empty ?? 'Nothing to show yet.'}
        </div>
      ) : (
        <>
          {/* ---- Desktop: semantic table ---- */}
          <div className="hidden overflow-x-auto sm:block">
            <table className="w-full border-collapse text-left">
              {caption && <caption className="sr-only">{caption}</caption>}
              <thead>
                <tr className="border-b border-brix-border bg-brix-surface-alt/60">
                  {columns.map((c) => {
                    const active = sort?.key === c.key;
                    return (
                      <th
                        key={c.key}
                        scope="col"
                        style={c.width ? { width: c.width } : undefined}
                        className={cn(
                          'px-4 py-3 text-xs font-semibold tracking-tight text-brix-muted',
                          alignClass(c),
                          c.headerClassName,
                        )}
                      >
                        {c.sortValue ? (
                          <button
                            type="button"
                            onClick={() => toggleSort(c.key)}
                            className={cn(
                              'inline-flex items-center gap-1 cursor-pointer hover:text-brix-ink transition-colors',
                              c.numeric || c.align === 'right' ? 'flex-row-reverse' : '',
                              active && 'text-brix-ink',
                            )}
                          >
                            {c.header}
                            {active ? (
                              sort!.dir === 'asc' ? (
                                <ArrowUp size={12} />
                              ) : (
                                <ArrowDown size={12} />
                              )
                            ) : (
                              <ChevronsUpDown size={12} className="opacity-40" />
                            )}
                          </button>
                        ) : (
                          c.header
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sorted.map((row) => {
                  const cells = columns.map((c) => (
                    <td
                      key={c.key}
                      className={cn(
                        'px-4 py-3.5 text-sm text-slate-900 align-middle',
                        alignClass(c),
                        c.numeric && 'font-medium tabular-nums',
                        c.className,
                      )}
                    >
                      {c.cell(row)}
                    </td>
                  ));
                  const rowCls = cn(
                    'border-b border-brix-border last:border-b-0 transition-colors',
                    interactive && 'cursor-pointer hover:bg-brix-primary-tint/30',
                  );
                  if (rowHref) {
                    // Whole row navigates; render as a Link on a display:contents
                    // wrapper so it stays a valid table row.
                    return (
                      <tr key={rowKey(row)} className={rowCls}>
                        {columns.map((c, i) => (
                          <td
                            key={c.key}
                            className={cn(
                              'p-0 align-middle',
                              c.className,
                            )}
                          >
                            <Link
                              to={rowHref(row)}
                              className={cn(
                                'block px-4 py-3.5 text-sm text-slate-900',
                                alignClass(c),
                                c.numeric && 'font-medium tabular-nums',
                                i === 0 && 'font-medium',
                              )}
                            >
                              {c.cell(row)}
                            </Link>
                          </td>
                        ))}
                      </tr>
                    );
                  }
                  return (
                    <tr
                      key={rowKey(row)}
                      className={rowCls}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                    >
                      {cells}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ---- Mobile: card stack ---- */}
          <div className="divide-y divide-brix-border sm:hidden">
            {sorted.map((row) => {
              const body = mobileRow ? (
                mobileRow(row)
              ) : (
                <div className="space-y-1.5">
                  {columns
                    .filter((c) => !c.hideOnMobile)
                    .map((c) => (
                      <div key={c.key} className="flex items-center justify-between gap-3">
                        <span className="text-xs font-semibold text-brix-muted">{c.header}</span>
                        <span className={cn('text-sm text-slate-900', c.numeric && 'tabular-nums')}>
                          {c.cell(row)}
                        </span>
                      </div>
                    ))}
                </div>
              );
              const cls = cn(
                'block px-4 py-3.5 transition-colors',
                interactive && 'cursor-pointer active:bg-brix-primary-tint/30',
              );
              if (rowHref) {
                return (
                  <Link key={rowKey(row)} to={rowHref(row)} className={cls}>
                    {body}
                  </Link>
                );
              }
              return (
                <div
                  key={rowKey(row)}
                  className={cls}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {body}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
